import {useState} from 'react'
import {useQuery, useMutation} from '@apollo/client'
import {Table, Button, Form, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {GET_SETTINGS_BY_ORGANIZATION} from '../../../gql/queries/settingsQuery'
import {CREATE_SETTING, UPDATE_SETTING, DELETE_SETTING} from '../../../gql/mutations/settingsMutations'
import {FormattedMessage, useIntl} from 'react-intl'
import {parseId} from '../../../helpers'
import {ORG_MESSAGES as M, SETTING_KEY_EXAMPLE} from '../organizationMessages'

interface ISettingNode {
  id: string
  name: string
  value: string
  loginReturn: boolean
}

interface Props {
  organizationId: string
}

const EMPTY_FORM = {name: '', value: ''}

export const OrganizationSettingsTab: React.FC<Props> = ({organizationId}) => {
  const relayOrganizationId = btoa(`organization_node:${organizationId}`)
  const {data, loading, refetch} = useQuery(GET_SETTINGS_BY_ORGANIZATION, {
    variables: {organization: relayOrganizationId},
    fetchPolicy: 'network-only',
  })
  const [createSetting] = useMutation(CREATE_SETTING)
  const [updateSetting] = useMutation(UPDATE_SETTING)
  const [deleteSetting] = useMutation(DELETE_SETTING)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({...EMPTY_FORM})
  const [showNewRow, setShowNewRow] = useState(false)
  const [newForm, setNewForm] = useState({...EMPTY_FORM})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const settings: ISettingNode[] = data?.allSettings?.edges?.map((e: any) => e.node) || []

  const intl = useIntl()
  const startEdit = (setting: ISettingNode) => {
    setEditingId(setting.id)
    setEditForm({name: setting.name, value: setting.value})
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({...EMPTY_FORM})
  }

  const handleCreate = async () => {
    if (!newForm.name.trim() || !newForm.value.trim()) {
      toast.warning(intl.formatMessage(M.fillNameAndValue))
      return
    }
    setSaving(true)
    try {
      const res = await createSetting({
        variables: {settingData: {name: newForm.name.trim(), value: newForm.value.trim(), organization: organizationId}},
      })
      const errors = res.data?.createSetting?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastSettingCreated))
        setNewForm({...EMPTY_FORM})
        setShowNewRow(false)
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errSettingCreate))
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    if (!editForm.name.trim() || !editForm.value.trim()) {
      toast.warning(intl.formatMessage(M.fillNameAndValue))
      return
    }
    setSaving(true)
    try {
      const res = await updateSetting({
        variables: {id: parseId(id), settingData: {name: editForm.name.trim(), value: editForm.value.trim(), organization: organizationId}},
      })
      const errors = res.data?.updateSetting?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastSettingUpdated))
        cancelEdit()
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errSettingUpdate))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(intl.formatMessage(M.confirmDeleteSetting))) return
    setDeletingId(id)
    try {
      const res = await deleteSetting({variables: {id: parseId(id)}})
      const errors = res.data?.deleteSetting?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastSettingDeleted))
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errSettingDelete))
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0" style={{fontSize: '0.8rem'}}>
          <FormattedMessage
            {...M.settingsHint}
            values={{example: SETTING_KEY_EXAMPLE, code: (chunks) => <code>{chunks}</code>}}
          />
        </p>
        {!showNewRow && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowNewRow(true)}>
            <i className="bi bi-plus-lg me-1"></i>
            {intl.formatMessage(M.addSetting)}
          </Button>
        )}
      </div>

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th style={{width: '35%'}}>{intl.formatMessage(M.name)}</th>
            <th style={{width: '35%'}}>{intl.formatMessage(M.thValue)}</th>
            <th style={{width: '30%'}} className="text-end">{intl.formatMessage(M.actions)}</th>
          </tr>
        </thead>
        <tbody>
          {showNewRow && (
            <tr>
              <td>
                <Form.Control
                  size="sm"
                  placeholder={intl.formatMessage(M.placeholderSettingName)}
                  value={newForm.name}
                  onChange={(e) => setNewForm((p) => ({...p, name: e.target.value}))}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  placeholder={intl.formatMessage(M.placeholderSettingValue)}
                  value={newForm.value}
                  onChange={(e) => setNewForm((p) => ({...p, value: e.target.value}))}
                />
              </td>
              <td className="text-end">
                <Button size="sm" variant="success" className="me-1" onClick={handleCreate} disabled={saving}>
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => {
                    setShowNewRow(false)
                    setNewForm({...EMPTY_FORM})
                  }}
                  disabled={saving}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </td>
            </tr>
          )}
          {settings.length === 0 && !showNewRow && (
            <tr>
              <td colSpan={3} className="text-center text-muted py-4">
                {intl.formatMessage(M.settingsEmpty)}
              </td>
            </tr>
          )}
          {settings.map((setting) => (
            <tr key={setting.id}>
              {editingId === setting.id ? (
                <>
                  <td>
                    <Form.Control
                      size="sm"
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({...p, name: e.target.value}))}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={editForm.value}
                      onChange={(e) => setEditForm((p) => ({...p, value: e.target.value}))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="success" className="me-1" onClick={() => handleUpdate(setting.id)} disabled={saving}>
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <Button size="sm" variant="light" onClick={cancelEdit} disabled={saving}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td>
                    <code>{setting.name}</code>
                  </td>
                  <td>{setting.value}</td>
                  <td className="text-end">
                    <Button size="sm" variant="light" className="me-1" onClick={() => startEdit(setting)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="text-danger"
                      onClick={() => handleDelete(setting.id)}
                      disabled={deletingId === setting.id}
                    >
                      {deletingId === setting.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash"></i>}
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}
